import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { join, map } from 'lodash-es';

import { animated } from '@react-spring/web';

import styles from './CoreViewer.module.scss';
import CoreLane from './components/CoreLane/CoreLane';
import { CoreLaneRowConfiguration } from './components/CoreLane/CoreLaneRows';
import { IntervalLaneRowContext } from './components/CoreLane/CoreLaneRows/IntervalLaneRow/IntervalLaneRowProvider';
import CoreLaneTooltip from './components/CoreLane/CoreLaneTooltip';
import FullRangeRuler from './components/FullRangeRuler';
import { useCoreViewerContext } from './context/CoreViewerContext';
import {
	ImageToRulerLengthAllocations,
	useValidatedImageToRulerLengthAllocations,
} from './helpers/image-to-ruler-allocation';
import useDragRows from './hooks/use-drag-rows';
import { useHoverLocation } from './hooks/use-hover-location';
import { laneRowSizeProvider } from './hooks/use-merged-image-type-loaders';
import { useThrottle } from './hooks/use-throttle';
import useWheelPanAndZoom from './hooks/use-wheel-pan-and-zoom';
import { ImageTypeLoaders } from './types/imageTypeLoader';
import { LengthRange } from './types/lengthRange';

/**
 * When the mouse hovers over a lane, this function is called with the depth of the lane (in image meters) that was hovered over.
 * If the lane is not hovered over, this function is called with undefined.
 * @param {number | undefined} at position (in image meters) of the cursor within the lane that was hovered over.
 */
export type HandleLaneMouseHoverFn = (atInImageMeters: number | undefined) => void;

export interface CoreViewerProps {
	viewerId: string;
	loaders: ImageTypeLoaders;
	mergedThicknessInPx: number;
	tooltip?: (viewerWidth: number) => React.ReactElement | undefined;
	onScaleFactorChange?: (scale: number) => void;
	isPrimary: boolean;
	configurations: CoreLaneRowConfiguration[];
	onLaneMouseHover?: HandleLaneMouseHoverFn;
	allowableRanges?: LengthRange[];
	allocations: ImageToRulerLengthAllocations;
	crosshairDepthM?: number;
}

export const CoreViewer: React.FC<CoreViewerProps> = ({
	viewerId,
	loaders,
	mergedThicknessInPx,
	tooltip,
	isPrimary,
	configurations,
	onLaneMouseHover,
	allowableRanges,
	allocations,
	crosshairDepthM,
}) => {
	const {
		zoomHandler,
		panHandler,
		setRange,
		horizontalRulerHeight,
		setHoverLaneRange,
		viewDimensions,
		addLoadingPercentageChangedHandler,
		removeLoadingPercentageChangedHandler,
		scale,
		range,
		horizontalOffset,
		visibleCoreLanesRanges,
		completelyVisibleLanesRange,
		getLaneYLocation,
	} = useCoreViewerContext();

	const validatedLengthAllocations = useValidatedImageToRulerLengthAllocations(allocations);

	const {
		viewerRef: _viewerRef,
		viewPortRef: _viewPortRef,
		viewerWidth: viewPortWidth,
		viewerHeight: viewPortHeight,
	} = viewDimensions;

	// Use a signature of loaded in configurations to prevent the heights in the hook below from constantly rendering
	const imageLoaderSignature = useMemo(
		() =>
			join(
				map(loaders, (value, prop) => ({ imageTypeId: +prop, metadata: value?.imageTypeMetadata }))
					.filter((l) => l.metadata)
					.map((l) => l.imageTypeId)
					.sort(),
				'|',
			),
		[loaders],
	);

	const configurationsAndHeights: (CoreLaneRowConfiguration & { height: number })[] = useMemo(
		() =>
			configurations.map((c) => {
				const size = laneRowSizeProvider(c, loaders, scale);
				const thickness = size?.thickness;
				if (!thickness) return { ...c, height: 0 };
				return {
					...c,
					height:
						!thickness.threshold || thickness.threshold < scale
							? (thickness.variableM ?? 0) * scale + (thickness.fixedPx ?? 0)
							: 0,
				};
			}),
		// signature is a proxy for loaders
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[configurations, imageLoaderSignature, scale],
	);

	const [isLoading, setIsLoading] = useState(false);
	useEffect(() => {
		addLoadingPercentageChangedHandler(viewerId, (loadingPercentage) =>
			setIsLoading(loadingPercentage !== undefined && loadingPercentage < 100),
		);
		return (): void => {
			removeLoadingPercentageChangedHandler(viewerId);
		};
	}, [addLoadingPercentageChangedHandler, removeLoadingPercentageChangedHandler, viewerId]);

	const internalViewerRef = useRef<HTMLDivElement>(null);
	const internalViewPortRef = useRef<SVGSVGElement>(null);
	//if we are viewport 1, use the provided ref for dimensions. Otherwise use an internal one for internal operations
	const viewerRef = isPrimary ? _viewerRef : internalViewerRef;
	const viewPortRef = isPrimary ? _viewPortRef : internalViewPortRef;

	useWheelPanAndZoom({
		ignoreMouseWheel: false,
		handleZoom: zoomHandler,
		handleVerticalScroll: (delta) => panHandler({ x: 0, y: delta }),
		target: viewerRef,
		// enabled: !imageTypeMetadataLoading,
		enabled: true,
	});

	const { isEditingInterval, isDrawingInterval } = useContext(IntervalLaneRowContext);

	const canDrag = !isEditingInterval && !isDrawingInterval;

	useDragRows({
		handleDrag: (xDelta: number, yDelta: number) => {
			if (canDrag) panHandler({ x: xDelta, y: yDelta * -1 });
		},
		target: viewerRef,
	});

	const laneHeight = mergedThicknessInPx;

	const hoveredLocation = useHoverLocation({
		target: viewPortRef,
		setHoverLaneRange,
		lanes: visibleCoreLanesRanges,
		getLaneYLocation,
		laneHeight,
		pxPerM: scale,
		horizontalRulerHeight,
	});

	// 20ms feels "natural" while minimising the number of updates/renders
	const throttledHoveredLocation = useThrottle(hoveredLocation, 20);

	useEffect(() => {
		onLaneMouseHover?.(throttledHoveredLocation.atDepthInM);
	}, [onLaneMouseHover, throttledHoveredLocation]);

	const { viewPortPositionInPx } = throttledHoveredLocation;

	return (
		<div className={clsx(styles.viewer, 'viewer')} data-is-loading={isLoading}>
			<div className={styles.coreContainer}>
				<div className={styles.core} ref={viewerRef} style={{ touchAction: 'none' }}>
					{viewPortWidth && viewPortHeight && (
						<>
							<svg width="100%" height="100%" ref={viewPortRef}>
								<>
									{viewPortWidth
										? visibleCoreLanesRanges.map((lane, idx) => (
												<animated.svg
													viewBox={`0 0 ${viewPortWidth} ${laneHeight}`}
													width={viewPortWidth}
													key={`tile-${idx}`}
													height={laneHeight}
													x={0}
													y={getLaneYLocation(lane)}
												>
													<CoreLane
														key={`lane-${idx}`}
														id={`lane-${idx}`}
														width={viewPortWidth}
														laneHeightInPx={laneHeight}
														lane={lane}
														scale={scale}
														rows={configurationsAndHeights}
														visibleRange={completelyVisibleLanesRange}
														allowableRanges={allowableRanges}
														crosshairDepthM={crosshairDepthM}
													/>
												</animated.svg>
											))
										: null}

									{viewPortPositionInPx && tooltip && (
										<CoreLaneTooltip
											x={viewPortPositionInPx[0]}
											y={viewPortPositionInPx[1]}
											viewPortWidth={viewPortWidth ?? 0}
											viewPortHeight={viewPortHeight ?? 0}
										>
											{tooltip(viewPortWidth)}
										</CoreLaneTooltip>
									)}
								</>
							</svg>

							<div>
								<FullRangeRuler
									offset={horizontalOffset ?? 0}
									xStart={viewPortHeight}
									lengthAllocations={validatedLengthAllocations}
									viewerHeightPx={viewPortHeight}
									imageVisibleRange={range}
									handlePan={(delta) => panHandler({ x: 0, y: delta })}
									setRange={setRange}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
